/**
 * Live Calls Service — WebSocket Edition
 * Connects to Swoole WebSocket for instant push updates.
 * Flow: fetch Swoole JWT → connect WebSocket → fetch initial snapshot → receive pushes.
 *
 * Same external API as polling version:
 *   connect(), disconnect(), onChange(cb), getActiveCalls(), isConnected()
 */

import { authService } from './auth-service.js';

const WS_TOKEN_ENDPOINT = '/voice/php/api/ws-token.php';
const SNAPSHOT_ENDPOINT = '/voice/php/api/live-events.php';
const HEARTBEAT_INTERVAL = 25000; // 25s (Swoole idle timeout is 60s)
const RECONNECT_BASE_DELAY = 2000;
const RECONNECT_MAX_DELAY = 30000;

class LiveCallsService {
  constructor() {
    /** @type {Map<number, object>} callId → call data */
    this._activeCalls = new Map();
    /** @type {Map<number, string>} callId → pipeline state */
    this._pipelineStates = new Map();
    /** @type {Set<Function>} */
    this._listeners = new Set();
    /** @type {Set<Function>} monitor log listeners */
    this._monitorListeners = new Set();
    /** @type {Set<Function>} monitor chat listeners */
    this._monitorChatListeners = new Set();
    /** @type {Set<Function>} monitor action listeners */
    this._monitorActionListeners = new Set();
    /** @type {Set<Function>} monitor proposal listeners */
    this._monitorProposalListeners = new Set();
    /** @type {Array<string>} recent monitor log lines */
    this._monitorLines = [];
    this._ws = null;
    this._connected = false;
    this._heartbeatTimer = null;
    this._reconnectTimer = null;
    this._reconnectAttempts = 0;
    this._intentionalClose = false;
  }

  /**
   * Start WebSocket connection to Swoole.
   */
  async connect() {
    if (this._ws) return;
    this._intentionalClose = false;
    await this._connectWebSocket();
  }

  /**
   * Stop WebSocket and clear state.
   */
  disconnect() {
    this._intentionalClose = true;
    this._cleanup();
    this._activeCalls.clear();
    this._pipelineStates.clear();
    this._connected = false;
    this._reconnectAttempts = 0;
    console.log('[LiveCalls] Disconnected');
    this._notify();
  }

  /**
   * Get active calls as array, merged with pipeline state.
   * @returns {Array<object>}
   */
  getActiveCalls() {
    return Array.from(this._activeCalls.values()).map(call => ({
      ...call,
      pipeline_state: this._pipelineStates.get(call.id) || 'listening'
    }));
  }

  /** @returns {number} */
  getActiveCallsCount() {
    return this._activeCalls.size;
  }

  /**
   * Optimistically remove a call from the active list (e.g., after hangup).
   * @param {number} callId
   */
  removeCall(callId) {
    this._activeCalls.delete(callId);
    this._pipelineStates.delete(callId);
    this._notify();
  }

  /** @returns {boolean} */
  isConnected() {
    return this._connected;
  }

  /**
   * Subscribe to state changes.
   * Callback receives (calls: Array, connected: boolean).
   * @returns {Function} unsubscribe
   */
  onChange(callback) {
    this._listeners.add(callback);
    return () => this._listeners.delete(callback);
  }

  // ── Internal: WebSocket lifecycle ──

  async _connectWebSocket() {
    try {
      // 1. Get Swoole JWT from auth bridge
      const tokenRes = await authService.apiFetch(WS_TOKEN_ENDPOINT);
      const tokenData = await tokenRes.json();

      if (!tokenData?.success || !tokenData?.ws_token) {
        throw new Error(tokenData?.error || 'Failed to get WebSocket token');
      }

      // 2. Build WebSocket URL from current hostname (no hardcoded URLs)
      const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsPath = tokenData.ws_path || 'voice-calls';
      const wsUrl = `${protocol}//${location.hostname}/ws/${wsPath}?authorization=${tokenData.ws_token}`;

      // 3. Connect
      this._ws = new WebSocket(wsUrl);

      this._ws.onopen = () => {
        console.log('[LiveCalls] WebSocket connected');
        this._reconnectAttempts = 0;
        this._startHeartbeat();
        // Fetch initial snapshot of active calls
        this._fetchSnapshot();
      };

      this._ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this._handleMessage(data);
        } catch (err) {
          console.warn('[LiveCalls] Bad message:', err);
        }
      };

      this._ws.onclose = (event) => {
        console.log('[LiveCalls] WebSocket closed', event.code);
        this._cleanup();
        this._connected = false;
        this._notify();

        if (!this._intentionalClose) {
          this._scheduleReconnect();
        }
      };

      this._ws.onerror = (err) => {
        console.warn('[LiveCalls] WebSocket error');
      };

    } catch (err) {
      console.error('[LiveCalls] Connection failed:', err.message);
      this._connected = false;
      this._notify();

      if (!this._intentionalClose) {
        this._scheduleReconnect();
      }
    }
  }

  _cleanup() {
    if (this._heartbeatTimer) {
      clearInterval(this._heartbeatTimer);
      this._heartbeatTimer = null;
    }
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }
    if (this._ws) {
      this._ws.onopen = null;
      this._ws.onmessage = null;
      this._ws.onclose = null;
      this._ws.onerror = null;
      if (this._ws.readyState === WebSocket.OPEN || this._ws.readyState === WebSocket.CONNECTING) {
        this._ws.close();
      }
      this._ws = null;
    }
  }

  _startHeartbeat() {
    if (this._heartbeatTimer) clearInterval(this._heartbeatTimer);
    this._heartbeatTimer = setInterval(() => {
      if (this._ws?.readyState === WebSocket.OPEN) {
        this._ws.send(JSON.stringify({ action: 'ping' }));
      }
    }, HEARTBEAT_INTERVAL);
  }

  _scheduleReconnect() {
    if (this._reconnectTimer) return;

    this._reconnectAttempts++;
    const delay = Math.min(
      RECONNECT_BASE_DELAY * Math.pow(2, this._reconnectAttempts - 1),
      RECONNECT_MAX_DELAY
    );

    console.log(`[LiveCalls] Reconnecting in ${delay}ms (attempt ${this._reconnectAttempts})`);
    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null;
      this._connectWebSocket();
    }, delay);
  }

  // ── Initial snapshot ──

  async _fetchSnapshot() {
    try {
      const res = await authService.apiFetch(`${SNAPSHOT_ENDPOINT}?lastEventId=0`);
      const data = await res.json();

      if (data?.success && Array.isArray(data.calls)) {
        this._activeCalls.clear();
        for (const call of data.calls) {
          this._activeCalls.set(call.id, {
            ...call,
            transcript: call.transcript || []
          });
        }
      }

      this._connected = true;
      this._notify();
      console.log(`[LiveCalls] Snapshot loaded: ${this._activeCalls.size} active calls`);
    } catch (err) {
      console.error('[LiveCalls] Snapshot fetch failed:', err.message);
      // Still mark as connected — WebSocket is up, just no initial data
      this._connected = true;
      this._notify();
    }
  }

  // ── WebSocket message handler ──

  _handleMessage(data) {
    // Welcome / pong — ignore
    if (data.voice_calls__welcome || data.voice_calls__pong) return;

    // Event push from Swoole
    if (data.voice_calls__event) {
      this._handleEvent(data.event_type, data.data || {});
    }
  }

  _handleEvent(type, eventData) {
    switch (type) {
      case 'call_started':
        this._onCallStarted(eventData);
        break;
      case 'call_ended':
        this._onCallEnded(eventData);
        break;
      case 'transcript_update':
        this._onTranscriptUpdate(eventData);
        break;
      case 'pipeline_state':
        this._onPipelineState(eventData);
        break;
      case 'tool_call':
        this._onToolCall(eventData);
        break;
      case 'monitor_log':
        this._onMonitorLog(eventData);
        break;
      case 'monitor_chat':
        this._onMonitorChat(eventData);
        break;
      case 'monitor_action':
        this._onMonitorAction(eventData);
        break;
      case 'monitor_proposal':
      case 'monitor_proposal_update':
        this._onMonitorProposal(eventData);
        break;
      default:
        // Unknown event — ignore
        break;
    }
  }

  _onCallStarted(data) {
    const call = {
      id: data.id,
      call_sid: data.call_sid,
      phone_number: data.phone_number,
      agent_id: data.agent_id,
      agent_name: data.agent_name,
      direction: data.direction || 'inbound',
      status: 'active',
      started_at: data.started_at,
      transcript: [],
      metadata: { flags: [], notes: [] }
    };
    this._activeCalls.set(call.id, call);
    this._pipelineStates.set(call.id, 'listening');
    this._notify();
  }

  _onCallEnded(data) {
    const callId = data.id;
    this._activeCalls.delete(callId);
    this._pipelineStates.delete(callId);
    this._notify();
  }

  _onTranscriptUpdate(data) {
    const callId = data.call_id;
    const call = this._activeCalls.get(callId);
    if (!call) return;

    // Add transcript chunk
    if (!call.transcript) call.transcript = [];
    call.transcript.push({
      speaker: data.speaker,
      content: data.content,
      timestamp_ms: data.timestamp_ms
    });

    this._notify();
  }

  _onPipelineState(data) {
    const callId = data.call_id;
    const state = data.state;
    if (!callId) return;

    this._pipelineStates.set(callId, state);
    this._notify();
  }

  _onToolCall(data) {
    // Tool calls also update pipeline state via separate pipeline_state events.
    // This event is for richer tool tracking if needed in the future.
  }

  _onMonitorLog(data) {
    const line = data.line || '';
    if (!line) return;
    this._monitorLines.push(line);
    // Cap at 500 lines to prevent memory growth
    if (this._monitorLines.length > 500) {
      this._monitorLines = this._monitorLines.slice(-500);
    }
    this._notifyMonitor(line);
  }

  /**
   * Subscribe to monitor log lines.
   * Callback receives (line: string).
   * @returns {Function} unsubscribe
   */
  onMonitorLog(callback) {
    this._monitorListeners.add(callback);
    return () => this._monitorListeners.delete(callback);
  }

  /** Get all buffered monitor log lines. */
  getMonitorLines() {
    return this._monitorLines;
  }

  /** Clear monitor log buffer. */
  clearMonitorLines() {
    this._monitorLines = [];
  }

  _notifyMonitor(line) {
    this._monitorListeners.forEach(cb => {
      try { cb(line); } catch (err) { console.error('[LiveCalls] Monitor listener error:', err); }
    });
  }

  // ── Monitor Chat ──

  _onMonitorChat(data) {
    this._monitorChatListeners.forEach(cb => {
      try { cb(data); } catch (err) { console.error('[LiveCalls] Chat listener error:', err); }
    });
  }

  /**
   * Subscribe to monitor chat events (typing, messages).
   * @returns {Function} unsubscribe
   */
  onMonitorChat(callback) {
    this._monitorChatListeners.add(callback);
    return () => this._monitorChatListeners.delete(callback);
  }

  // ── Monitor Actions ──

  _onMonitorAction(data) {
    this._monitorActionListeners.forEach(cb => {
      try { cb(data); } catch (err) { console.error('[LiveCalls] Action listener error:', err); }
    });
  }

  onMonitorAction(callback) {
    this._monitorActionListeners.add(callback);
    return () => this._monitorActionListeners.delete(callback);
  }

  // ── Monitor Proposals ──

  _onMonitorProposal(data) {
    this._monitorProposalListeners.forEach(cb => {
      try { cb(data); } catch (err) { console.error('[LiveCalls] Proposal listener error:', err); }
    });
  }

  onMonitorProposal(callback) {
    this._monitorProposalListeners.add(callback);
    return () => this._monitorProposalListeners.delete(callback);
  }

  // ── Notify listeners ──

  _notify() {
    const calls = this.getActiveCalls();
    const connected = this._connected;
    this._listeners.forEach(cb => {
      try {
        cb(calls, connected);
      } catch (err) {
        console.error('[LiveCalls] Listener error:', err);
      }
    });
  }
}

export const liveCallsService = new LiveCallsService();
